/**
 * Sayfa görselleri: kayıt tipleri ve yardımcılar (veri yok).
 *
 * Veri şablon başına ayrı modüllerde durur: client/src/lib/sayfa_gorsel_veri/<şablon>.ts (URL yolu → hero kapağı +
 * ara görseller). Her şablon (HizmetDetay, AjansLanding, PlatformHizmet, PlatformHub, SektorSayfasi, statik sektör
 * sayfaları, şehir, İstanbul, GlossaryDetail, Prices, About) YALNIZ kendi modülünü içe aktarır; böylece kayıtlar o
 * şablonun parçasına girer, sayfa için ayrı bir harita isteği olmaz ve başka şablonların kayıtları indirilmez. Ek içerik
 * bölümüne bağlı görseller sayfa_gorsel_veri/ek_bolum.ts'tedir (Layout'taki EkIcerikOto yalnız gereken yolda yükler).
 * Birleşik harita (SSR server/seo/render.ts ve testler): client/src/lib/sayfa_gorsel.ts; istemci kodu onu içe aktarmaz.
 * Kaydı olmayan sayfanın DOM'u ve SSR çıktısı birebir aynı kalır.
 *
 * Dosyalar (scripts/gorsel/README.md "Sayfa görselleri"):
 *   client/public/sayfa-gorsel<URL yolu>/kapak.webp      1200x630, ≤ 90 KB (hedef ≤ 45 KB); og:image olarak da kullanılır
 *   client/public/sayfa-gorsel<URL yolu>/kapak-720.webp  720x378, ≤ 25 KB  (node scripts/gorsel/varyant.mjs)
 *   client/public/sayfa-gorsel<URL yolu>/kapak-480.webp  480x252, ≤ 15 KB
 *   client/public/sayfa-gorsel<URL yolu>/sekil-1.webp    1200x675, ≤ 120 KB (ara görsel)
 * Yayındaki bir görsel değişirse aynı adın üzerine yazılmaz, yeni ad kullanılır (kapak-v2.webp): statik dosyalar 30 gün
 * önbelleklenir.
 *
 * Örnek kayıt (sayfa_gorsel_veri/hizmet_detay.ts):
 *   "/hizmetlerimiz/seo-geo": {
 *     kapak: { src: "/sayfa-gorsel/hizmetlerimiz/seo-geo/kapak.webp", alt: "…", w: 1200, h: 630 },
 *     ara: [{ src: "/sayfa-gorsel/hizmetlerimiz/seo-geo/sekil-1.webp", alt: "…", w: 1200, h: 675, altYazi: "…" }],
 *   },
 * Denetim: server/seo/sayfa_gorsel.test.ts (dosya, gerçek boyut, varyantlar, KB sınırı, alt metin, dondurulmuş yollar,
 * kaydın doğru şablon modülünde olması, SSR basımı). Dondurulmuş yollar: client/src/lib/gorsel_dondurma.ts.
 */

export interface SayfaGorsel {
  /** "/sayfa-gorsel<URL yolu>/kapak.webp" ya da ".../sekil-1.webp" */
  src: string;
  /** Görselin ne gösterdiği; ≥ 15 karakter, "görsel"/"resim" diye başlamaz */
  alt: string;
  /** Gerçek piksel boyutu: kapak 1200x630, ara görsel 1200x675 */
  w: number;
  h: number;
  /** Ara görselde isteğe bağlı alt yazı (figcaption) */
  altYazi?: string;
  /**
   * Yalnız ek içerik görselinde (sayfa_gorsel_veri/ek_bolum.ts): sayfanın ek içerik bölümünün H2 başlığı
   * (IcerikBolumu.baslik, birebir). Görsel şablonun ara yuvası yerine o bölümün paragraflarından sonra basılır
   * (components/EkIcerik.tsx, render.ts ekIcerikHtml).
   */
  bolum?: string;
}

export interface SayfaGorselKaydi {
  /** Hero sağ sütun kapağı (1200x630); og:image, twitter:image ve Service JSON-LD image olur */
  kapak?: SayfaGorsel;
  /** Ara görseller (1200x675); `bolum`u olmayanlar şablonun ara yuvasında sırayla basılır */
  ara?: SayfaGorsel[];
}

/** URL yolu (sondaki "/" olmadan, ör. "/hizmetlerimiz/seo-geo") → görseller */
export type SayfaGorselHaritasi = Record<string, SayfaGorselKaydi>;

/** Sayfa görsellerinin kök klasörü (client/public altında) */
export const SAYFA_GORSEL_ONEKI = "/sayfa-gorsel";

/** Sorgu, çapa ve sondaki "/" atılmış yol ("" → "/") */
export function gorselYolu(yol: string): string {
  return yol.split(/[?#]/)[0].replace(/\/+$/, "") || "/";
}

/** Haritadaki kayıt (yol gorselYolu ile temizlenir); kayıt yoksa undefined. */
export function kayitBul(
  harita: SayfaGorselHaritasi,
  yol: string
): SayfaGorselKaydi | undefined {
  const temiz = gorselYolu(yol);
  return Object.prototype.hasOwnProperty.call(harita, temiz)
    ? harita[temiz]
    : undefined;
}

/** Şablonun kendi ara yuvasına düşen görseller (ek içerik bölümüne bağlı olmayanlar) */
export function sablonAraGorselleri(
  k: SayfaGorselKaydi | undefined
): SayfaGorsel[] {
  return (k?.ara ?? []).filter(g => !g.bolum);
}

/** Ek içerik bölümüne (H2 başlığına) bağlı ara görseller */
export function bolumAraGorselleri(
  k: SayfaGorselKaydi | undefined,
  bolumBasligi: string
): SayfaGorsel[] {
  return (k?.ara ?? []).filter(g => g.bolum === bolumBasligi);
}

/** Kapak srcset genişlikleri (asıl dosya 1200w). 600w yok: mobilde (380 css px × DPR 1,75 = 665) hiç seçilmez. */
export const KAPAK_VARYANTLARI = [480, 720] as const;

/** "/…/kapak.webp" → "/…/kapak-720.webp" */
export function varyantYolu(src: string, genislik: number): string {
  return src.replace(/\.webp$/i, `-${genislik}.webp`);
}

/** Kapak srcset'i: "…/kapak-480.webp 480w, …/kapak-720.webp 720w, …/kapak.webp 1200w" */
export function gorselSrcset(src: string, asilGenislik = 1200): string {
  return [
    ...KAPAK_VARYANTLARI.map(w => `${varyantYolu(src, w)} ${w}w`),
    `${src} ${asilGenislik}w`,
  ].join(", ");
}

/** Kapak en fazla 400 px gösterilir; dar ekranda sütun genişliği (16 px yan boşluk). */
export const KAPAK_SIZES = "(min-width: 432px) 400px, calc(100vw - 32px)";
